import { useEffect, useRef, useState } from 'react';
import type { Profesional } from '../../../api/ops/citas';
import { ErrorApi } from '../../../api/problema';
import { Ayuda } from '../../../componentes/Ayuda';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Boton } from '../../../componentes/Boton';
import { Campo } from '../../../componentes/Campo';
import { Cifra } from '../../../componentes/Cifra';
import { Icono } from '../../../componentes/Icono';
import { Interruptor } from '../../../componentes/Interruptor';
import { OpcionRadio } from '../../../componentes/OpcionRadio';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { Ventana } from '../../../componentes/Ventana';
import { DIAS, hoyEn, sumarDias, diaDeLaSemana, fechaLarga } from '../../../logica/horario';
import { iniciales } from '../../../logica/navegacion';
import { Avatar } from '../../../marco/piezas';
import type { PropsPantalla } from '../../tipos';
import { hm, mayuscula, textoDeError, tramosDelDia } from '../../agenda/logica';
import type { DatosServicios } from '../datos';
import { COLORES, primerNombre, textoDuracion, unir } from '../logica';
import { AYUDA, SOLO_LECTURA, SOLO_VER } from '../textos';
import {
    ACCESO,
    PESTANAS_PROFESIONAL,
    altaProfesional,
    camposProfesional,
    citasPendientes,
    emailOculto,
    emailValido,
    errorFoto,
    erroresProfesional,
    formDe,
    formVacio,
    horarioDe,
    type FormProfesional,
    type PestanaProfesional,
} from './logica';

interface Props extends PropsPantalla {
    datos: DatosServicios;
    profesional: Profesional | null;
    horario: 'negocio' | 'propio' | null;
    pestana: PestanaProfesional;
    gestiona: boolean;
    propietario: boolean;
    onContratar: () => void;
    onPestana: (p: PestanaProfesional) => void;
    onCerrar: () => void;
    onCreado: (p: Profesional) => void;
    onCambiado: (p: Profesional) => void;
    onHorarios: (id: string) => void;
}

const fallo412 = 'Alguien lo ha cambiado mientras tanto. Cierra la ficha y vuelve a abrirla.';

/**
 * Ficha del profesional (9.6) en RV Panel. Nuevo: datos con su foto (G493, G505), servicios que hace, su horario (el del
 * negocio o uno propio) y si tendrá acceso al panel (invitación con rol Profesional, 7 días, G483); si no cabe en el plan,
 * se guarda desactivado (`saved_inactive_reason`). Abierto, su botonera Datos · Servicios · Acceso · Horario
 * (`&tab=`). Desactivar pregunta qué pasa con sus citas futuras (`409 commitments_pending` con la lista: recolocarlas o
 * cancelarlas); activar solo si cabe en el plan (`entitlement_required`).
 */
export function FichaProfesional(props: Props) {
    const { ops, negocio, movil, soloLectura, datos, profesional, pestana, gestiona, propietario, onContratar, onPestana, onCerrar, onCreado, onCambiado } = props;
    const avisar = useAvisoBreve();
    const nuevo = profesional === null;
    const [version, setVersion] = useState<string | null>(null);
    const [form, setForm] = useState<FormProfesional>(() => (profesional === null ? formVacio() : formDe(profesional)));
    const [hace, setHace] = useState<string[]>(() => profesional?.service_ids ?? []);
    const [probado, setProbado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [errorDni, setErrorDni] = useState<string | null>(null);
    const [ventana, setVentana] = useState<'baja' | 'acceso' | 'plan' | null>(null);

    useEffect(() => {
        if (profesional === null) {
            return;
        }
        let vivo = true;
        ops.getProfessional(negocio, profesional.id)
            .then((r) => {
                if (vivo) {
                    setVersion(r.version);
                    onCambiado(r.datos);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
        // Solo al abrir: después, la versión la da cada respuesta.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, negocio, profesional?.id]);

    const activos = datos.profesionales.filter((p) => p.status === 'active');
    const limite = datos.limite ?? Number.POSITIVE_INFINITY;
    const cabe = activos.length < limite;
    const fallo = (e: unknown) => {
        avisar({ tipo: 'error', texto: e instanceof ErrorApi && e.estado === 412 ? fallo412 : textoDeError(e) });
    };
    const errorDeDni = (e: unknown): boolean => {
        if (e instanceof ErrorApi && e.errores.some((x) => x.field === 'national_id')) {
            setErrorDni(e.errores.find((x) => x.field === 'national_id')?.message ?? 'Ese DNI ya es de otra persona de tu equipo.');
            return true;
        }
        return false;
    };

    const guardarDatos = () => {
        setProbado(true);
        if (Object.keys(erroresProfesional(form, nuevo)).length > 0) {
            return;
        }
        setOcupado(true);
        setErrorDni(null);
        if (profesional === null) {
            ops.createProfessional(negocio, altaProfesional(form))
                .then((r) => {
                    const nombre = r.professional.display_name;
                    if (r.saved_inactive_reason === 'plan_limit') {
                        avisar({ tipo: 'ok', texto: `${nombre} se ha guardado desactivado: ya tienes ${String(activos.length)} de ${String(limite)} profesionales activos. Actívalo cuando contrates un profesional extra o desactives a otro.` });
                    } else if (form.acceso) {
                        avisar({ tipo: 'ok', texto: `${nombre} ya está en tu equipo. Le hemos enviado la invitación a ${form.email.trim()}: caduca a los 7 días.` });
                    } else {
                        avisar({ tipo: 'ok', texto: `${nombre} ya está en tu equipo y puede recibir citas.` });
                    }
                    onCreado(r.professional);
                })
                .catch((e: unknown) => {
                    if (!errorDeDni(e)) {
                        fallo(e);
                    }
                })
                .finally(() => { setOcupado(false); });
            return;
        }
        if (version === null) {
            setOcupado(false);
            return;
        }
        ops.updateProfessional(negocio, profesional.id, camposProfesional(form), version)
            .then((r) => {
                setVersion(r.version);
                onCambiado(r.datos);
                avisar({ tipo: 'ok', texto: 'Datos guardados.' });
            })
            .catch((e: unknown) => {
                if (!errorDeDni(e)) {
                    fallo(e);
                }
            })
            .finally(() => { setOcupado(false); });
    };

    const guardarServicios = () => {
        if (profesional === null || version === null) {
            return;
        }
        setOcupado(true);
        ops.setProfessionalSkills(negocio, profesional.id, hace, version)
            .then((r) => {
                setVersion(r.version);
                onCambiado(r.datos);
                avisar({ tipo: 'ok', texto: 'Servicios guardados. Las citas ya hechas no cambian.' });
            })
            .catch(fallo)
            .finally(() => { setOcupado(false); });
    };

    const activar = () => {
        if (profesional === null) {
            return;
        }
        if (!cabe) {
            setVentana('plan');
            return;
        }
        setOcupado(true);
        ops.activateProfessional(negocio, profesional.id)
            .then((p) => {
                onCambiado(p);
                avisar({ tipo: 'ok', texto: `${p.display_name} vuelve a recibir citas.` });
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.codigo === 'entitlement_required') {
                    setVentana('plan');
                    return;
                }
                fallo(e);
            })
            .finally(() => { setOcupado(false); });
    };

    const p = profesional;
    const acceso = p === null ? null : ACCESO[p.panel_access];
    const accion = !gestiona ? '' : nuevo ? 'Dar de alta' : pestana === 'datos' || pestana === 'servicios' ? 'Guardar' : '';
    const ayuda = nuevo ? AYUDA.proNew : { datos: AYUDA.proDatos, servicios: AYUDA.proSvcs, acceso: AYUDA.acceso, horario: AYUDA.horario }[pestana];

    const contenido = (() => {
        if (nuevo || (pestana === 'datos' && gestiona)) {
            return (
                <FormDatos
                    {...props}
                    form={form}
                    setForm={setForm}
                    probado={probado}
                    errorDni={errorDni}
                    cabe={cabe}
                    activos={activos.length}
                    limite={limite}
                    onActivar={activar}
                    onDesactivar={() => { setVentana('baja'); }}
                    ocupado={ocupado}
                />
            );
        }
        if (p === null) {
            return null;
        }
        if (pestana === 'datos') {
            return (
                <div className="rv-sv-lineas">
                    {[
                        ['Nombre', p.display_name],
                        ['Teléfono', p.phone ?? '—'],
                        ['Email', p.email ?? '—'],
                        ['En la web y el widget', p.online_bookable ? 'Se puede elegir' : 'Solo desde el panel'],
                        ['Estado', p.status === 'active' ? 'Activo' : 'Desactivado'],
                    ].map(([k, v]) => (
                        <div key={k} className="rv-sv-lineas__l">
                            <span>{k}</span>
                            <span>{v}</span>
                        </div>
                    ))}
                </div>
            );
        }
        if (pestana === 'servicios') {
            const activosSvc = datos.servicios.filter((s) => s.status === 'active');
            const huerfanos = activosSvc.filter((s) => p.service_ids.includes(s.id) && !hace.includes(s.id) && !s.professional_ids.some((id) => id !== p.id && datos.profesionales.find((x) => x.id === id)?.status === 'active'));
            return (
                <div className="rv-sv-form">
                    <p className="rv-sv-nota">Los servicios que puede hacer. Solo se le ofrecen citas de estos.</p>
                    <div>
                        {activosSvc.map((s) => (
                            <Interruptor
                                key={s.id}
                                etiqueta={s.current_version.name}
                                ayuda={[s.current_version.category ?? '', textoDuracion(s.current_version.base_duration_minutes)].filter((x) => x !== '').join(' · ')}
                                valor={hace.includes(s.id)}
                                desactivado={!gestiona}
                                compacto
                                onChange={(v) => { setHace((h) => (v ? [...h, s.id] : h.filter((x) => x !== s.id))); }}
                            />
                        ))}
                    </div>
                    {huerfanos.length > 0 && p.status === 'active' && (
                        <div className="rv-sv-alerta" role="status">
                            <Icono nombre="triangle-alert" />
                            <span>{`${unir(huerfanos.map((s) => `«${s.current_version.name}»`))} ${huerfanos.length === 1 ? 'se queda' : 'se quedan'} sin nadie que lo haga: dejará de poder pedirse.`}</span>
                        </div>
                    )}
                </div>
            );
        }
        if (pestana === 'acceso' && acceso !== null) {
            const texto =
                p.panel_access === 'granted'
                    ? { titulo: 'Entra al panel', texto: `Entra con su cuenta (${emailOculto(p.email)}) y rol Profesional: ve su agenda y sus citas.`, pie: 'Retirarle el acceso se hace en Equipo y roles. Sigue recibiendo citas.' }
                    : p.panel_access === 'invited'
                      ? { titulo: 'Invitación enviada', texto: `Enviada a ${emailOculto(p.email)}. Al aceptarla, entra con rol Profesional.`, pie: 'Para reenviarla o anularla, ve a Equipo y roles.' }
                      : { titulo: 'Sin acceso al panel', texto: 'Recibe citas, pero no entra al panel: su agenda la lleváis vosotros.', pie: 'Le llega una invitación con rol Profesional. Su contraseña la elige al aceptarla.' };
            return (
                <div className="rv-sv-form">
                    <div className="rv-sv-acceso">
                        <span className={`rv-sv-baldosa rv-sv-acceso--${p.panel_access}`}>
                            <Icono nombre={p.panel_access === 'invited' ? 'send' : 'key-round'} />
                        </span>
                        <span className="rv-sv-celda__texto">
                            <strong>{texto.titulo}</strong>
                            <small>{texto.texto}</small>
                        </span>
                    </div>
                    <div className="rv-sv-botones" style={{ justifyContent: 'flex-start' }}>
                        {p.panel_access === 'none' && gestiona && (
                            <Boton icono="send" movil={movil} onClick={() => { setVentana('acceso'); }}>
                                Darle acceso al panel
                            </Boton>
                        )}
                        <Boton tipo="secundario" icono="user-cog" movil={movil} onClick={() => { props.navegar('/equipo'); }}>
                            Ver en Equipo y roles
                        </Boton>
                    </div>
                    <p className="rv-sv-nota">{texto.pie}</p>
                </div>
            );
        }
        return <SuHorario {...props} profesional={p} />;
    })();

    return (
        <>
            <PanelLateral
                abierto
                onCerrar={onCerrar}
                movil={movil}
                tamano={nuevo ? 'formulario' : 'ficha'}
                eyebrow={nuevo ? 'Nuevo profesional' : 'Profesional'}
                titulo={nuevo ? (form.nombre.trim() === '' ? 'Nuevo profesional' : form.nombre.trim()) : (p?.display_name ?? '')}
                subtitulo={nuevo ? 'Recibe citas en cuanto le pongas servicios.' : p?.status === 'active' ? 'Activo' : 'Desactivado'}
                {...(acceso !== null ? { chip: acceso.texto, chipEstado: acceso.chip } : {})}
                icono="user-plus"
                accion={accion}
                accionIcono={nuevo ? 'user-plus' : 'check'}
                ocupado={ocupado}
                inactiva={!nuevo && version === null}
                onAccion={pestana === 'servicios' && !nuevo ? guardarServicios : guardarDatos}
            >
                <div className={`rv-sv-ficha${nuevo ? ' rv-sv-ficha--sola' : ''}`}>
                    {!nuevo && (
                        <div className="rv-sv-botonera" role="tablist" aria-label="Apartados del profesional">
                            {PESTANAS_PROFESIONAL.map(([clave, texto, icono]) => (
                                <button key={clave} type="button" role="tab" aria-selected={pestana === clave} className="rv-sv-botonera__b" onClick={() => { onPestana(clave); }}>
                                    <Icono nombre={icono} />
                                    <span>{texto}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="rv-sv-ficha__contenido">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <h3 className="rv-sv-ceja">{nuevo ? 'Datos' : (PESTANAS_PROFESIONAL.find(([x]) => x === pestana)?.[1] ?? '')}</h3>
                            <span style={{ flex: 1 }} />
                            <Ayuda tarjetas={ayuda} />
                        </div>
                        {!nuevo && !gestiona && pestana !== 'horario' && (
                            <p className="rv-sv-nota">
                                <Icono nombre="eye" />
                                <span>{soloLectura ? SOLO_LECTURA : SOLO_VER}</span>
                            </p>
                        )}
                        {contenido}
                    </div>
                </div>
            </PanelLateral>
            {p !== null && (
                <VentanaBaja
                    {...props}
                    abierta={ventana === 'baja'}
                    profesional={p}
                    onCerrar={() => { setVentana(null); }}
                    onHecho={(x) => {
                        setVentana(null);
                        onCambiado(x);
                    }}
                />
            )}
            {p !== null && (
                <VentanaAcceso
                    {...props}
                    abierta={ventana === 'acceso'}
                    profesional={p}
                    onCerrar={() => { setVentana(null); }}
                    onHecho={(x) => {
                        setVentana(null);
                        onCambiado(x);
                    }}
                />
            )}
            <Ventana
                abierta={ventana === 'plan'}
                titulo="No cabe en tu plan"
                texto={`Ya tienes ${String(activos.length)} de ${String(limite)} profesionales activos. Para activarlo, contrata un profesional extra o desactiva a otro.${propietario ? '' : ' Pídeselo al propietario.'}`}
                {...(propietario ? { accion: 'Contratar profesional extra', accionIcono: 'user-plus' as const, onAccion: onContratar } : {})}
                movil={movil}
                onCerrar={() => { setVentana(null); }}
            />
        </>
    );
}

// ---- Datos (alta y ficha) ----

function FormDatos({
    ops,
    negocio,
    datos,
    profesional,
    form,
    setForm,
    probado,
    errorDni,
    cabe,
    activos,
    limite,
    propietario,
    onContratar,
    onActivar,
    onDesactivar,
    ocupado,
}: Props & {
    form: FormProfesional;
    setForm: (f: (x: FormProfesional) => FormProfesional) => void;
    probado: boolean;
    errorDni: string | null;
    cabe: boolean;
    activos: number;
    limite: number;
    onActivar: () => void;
    onDesactivar: () => void;
    ocupado: boolean;
}) {
    const avisar = useAvisoBreve();
    const archivo = useRef<HTMLInputElement>(null);
    const [subiendo, setSubiendo] = useState(false);
    const nuevo = profesional === null;
    const e = probado ? erroresProfesional(form, nuevo) : {};
    const pon =
        <K extends keyof FormProfesional>(k: K) =>
        (v: FormProfesional[K]) => {
            setForm((f) => ({ ...f, [k]: v }));
        };

    const subirFoto = (f: File | undefined) => {
        if (f === undefined) {
            return;
        }
        const malo = errorFoto(f);
        if (malo !== null) {
            avisar({ tipo: 'error', texto: malo });
            return;
        }
        setSubiendo(true);
        ops.uploadProfessionalPhoto(negocio, f, f.name)
            .then((r) => {
                const vista = typeof URL.createObjectURL === 'function' ? URL.createObjectURL(f) : '';
                setForm((x) => ({ ...x, foto: { ref: r.photo_upload_ref, vista, nombre: f.name } }));
            })
            .catch((err: unknown) => { avisar({ tipo: 'error', texto: textoDeError(err, 'No he podido subir la foto. Prueba de nuevo.') }); })
            .finally(() => { setSubiendo(false); });
    };

    const conFoto = form.foto !== null || (profesional?.photo_url ?? null) !== null;
    const verFoto = form.foto?.vista ?? profesional?.photo_url ?? null;

    return (
        <div className="rv-sv-form">
            {nuevo && !cabe && (
                <div className="rv-sv-alerta" role="status">
                    <Icono nombre="triangle-alert" />
                    <span>
                        {`Ya tienes ${String(activos)} de ${String(limite)} profesionales activos. Se guardará desactivado hasta que contrates un profesional extra o desactives a otro.${propietario ? '' : ' Pídeselo al propietario.'}`}
                        {propietario && (
                            <span style={{ display: 'block', marginTop: 8 }}>
                                <Boton tipo="secundario" icono="user-plus" onClick={onContratar}>
                                    Contratar profesional extra
                                </Boton>
                            </span>
                        )}
                    </span>
                </div>
            )}
            <div className="rv-sv-foto-fila">
                {verFoto !== null && verFoto !== '' ? <img className="rv-sv-foto rv-sv-foto--grande" src={verFoto} alt="" width={72} height={72} /> : <Avatar texto={iniciales(form.nombre.trim() === '' ? '?' : form.nombre)} tamano={72} fondo={form.color} tinta="#fff" letra={22} />}
                <span className="rv-sv-celda__texto">
                    <Boton tipo="secundario" icono="camera" ocupado={subiendo} ocupadoTexto="Subiendo…" onClick={() => archivo.current?.click()}>
                        {conFoto ? 'Cambiar la foto' : 'Subir una foto'}
                    </Boton>
                    <small>{form.foto !== null ? `${form.foto.nombre} · recortada cuadrada. Sale en la web y el widget al pedir cita.` : conFoto ? 'Recortada cuadrada. Sale en la web y el widget al pedir cita.' : 'PNG, JPEG o WebP de hasta 2 MB. La recortamos cuadrada. Sin foto, se ven sus iniciales.'}</small>
                </span>
                <input
                    ref={archivo}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="rv-sr"
                    aria-label="Foto del profesional"
                    tabIndex={-1}
                    onChange={(ev) => {
                        subirFoto(ev.target.files?.[0]);
                        ev.target.value = '';
                    }}
                />
            </div>
            <Campo etiqueta="Nombre y apellidos" valor={form.nombre} onChange={pon('nombre')} error={e.nombre ?? null} ayuda="Así lo verán tus clientes al pedir cita." autocompletar="off" mayusculas="words" max={80} nombre="profesional-nombre" />
            <Campo etiqueta="Teléfono" tipo="tel" valor={form.telefono} onChange={pon('telefono')} error={e.telefono ?? null} autocompletar="off" max={20} nombre="profesional-telefono" />
            <Campo
                etiqueta="Email"
                tipo="email"
                valor={form.email}
                onChange={pon('email')}
                error={e.email ?? null}
                ayuda={nuevo ? 'Para avisarle de sus citas y, si tendrá acceso, para invitarle.' : profesional.panel_access === 'granted' ? 'El de su cuenta lo cambia él desde Mi cuenta.' : 'Para avisarle de sus citas.'}
                autocompletar="off"
                nombre="profesional-email"
            />
            {form.conDni ? (
                <Campo etiqueta="DNI (opcional)" valor={form.dni} onChange={pon('dni')} error={errorDni} ayuda="Solo lo ve tu negocio. No puede repetirse en tu equipo." autocompletar="off" mayusculas="characters" max={20} />
            ) : (
                <button type="button" className="rv-sv-enlace" onClick={() => { pon('conDni')(true); }}>
                    <Icono nombre="plus" />
                    <span>{profesional?.national_id_masked != null ? `Cambiar su DNI (${profesional.national_id_masked})` : 'Añadir su DNI'}</span>
                </button>
            )}
            <div className="rv-sv-grupo">
                <span className="rv-sv-grupo__t">Color en la agenda</span>
                <div role="radiogroup" aria-label="Color en la agenda" className="rv-sv-colores">
                    {COLORES.map((c) => (
                        <button key={c.hex} type="button" role="radio" aria-checked={form.color.toLowerCase() === c.hex.toLowerCase()} aria-label={c.nombre} style={{ background: c.hex }} onClick={() => { pon('color')(c.hex); }}>
                            <Icono nombre="check" />
                        </button>
                    ))}
                </div>
            </div>
            <div className="rv-sv-grupo">
                <span className="rv-sv-grupo__t">En tus canales</span>
                <Interruptor etiqueta="Se puede elegir en la web y en el widget" ayuda="Apagado, solo le dais citas desde el panel." valor={form.enLinea} onChange={pon('enLinea')} />
            </div>
            {nuevo && (
                <>
                    <div className="rv-sv-grupo">
                        <span className="rv-sv-grupo__t">Servicios que hace</span>
                        <div>
                            {datos.servicios
                                .filter((s) => s.status === 'active')
                                .map((s) => (
                                    <Interruptor key={s.id} etiqueta={s.current_version.name} valor={form.servicios.includes(s.id)} compacto onChange={(v) => { pon('servicios')(v ? [...form.servicios, s.id] : form.servicios.filter((x) => x !== s.id)); }} />
                                ))}
                        </div>
                        {e.servicios === true && (
                            <span role="alert" className="rv-campo__error">
                                <Icono nombre="circle-alert" />
                                <span>Elige al menos un servicio.</span>
                            </span>
                        )}
                    </div>
                    <div className="rv-sv-grupo" role="radiogroup" aria-label="Su horario">
                        <span className="rv-sv-grupo__t">Su horario</span>
                        <OpcionRadio elegida={form.calendario === 'inherit'} titulo="Como el negocio" texto="Trabaja cuando abre el negocio. Lo cambias después en Horarios." onElegir={() => { pon('calendario')('inherit'); }} />
                        <OpcionRadio elegida={form.calendario === 'own'} titulo="Uno propio" texto="Empieza con una copia de la semana del negocio para ajustarla." onElegir={() => { pon('calendario')('own'); }} />
                    </div>
                    <div className="rv-sv-grupo">
                        <span className="rv-sv-grupo__t">Acceso al panel</span>
                        <Interruptor
                            etiqueta="Tendrá acceso al panel"
                            ayuda={form.acceso ? 'Le llega una invitación con rol Profesional que caduca a los 7 días. Su contraseña la elige al aceptarla.' : 'Sin acceso, vuestro equipo lleva su agenda.'}
                            {...(e.acceso === undefined ? {} : { error: e.acceso })}
                            valor={form.acceso}
                            onChange={pon('acceso')}
                        />
                        <p className="rv-sv-nota">Sin acceso también recibe citas: su agenda la lleváis desde el panel. Su invitación, sus roles y su acceso se gestionan en Equipo y roles.</p>
                    </div>
                </>
            )}
            {!nuevo && (
                <div className="rv-sv-pie">
                    {profesional.status === 'active' ? (
                        <>
                            <Boton tipo="secundario" rojo icono="user-x" ocupado={ocupado} onClick={onDesactivar}>
                                Desactivar
                            </Boton>
                            <span>Deja de recibir citas y de contar para tu plan. No se borra: sus citas pasadas, cobros y notas se conservan.</span>
                        </>
                    ) : (
                        <>
                            <Boton tipo="secundario" icono="user-check" ocupado={ocupado} onClick={onActivar}>
                                Activar
                            </Boton>
                            <span>{cabe ? 'Vuelve a recibir citas con su horario y sus servicios.' : `Tu plan está completo (${String(activos)} de ${String(limite)}): para activarlo, contrata un profesional extra o desactiva a otro.`}</span>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// ---- Desactivar (409 commitments_pending con sus citas futuras) ----

function VentanaBaja({ ops, negocio, movil, profesional, abierta, onCerrar, onHecho, navegar, ruta }: Props & { profesional: Profesional; abierta: boolean; onHecho: (p: Profesional) => void }) {
    const avisar = useAvisoBreve();
    const [citas, setCitas] = useState<ReturnType<typeof citasPendientes>>(null);
    const [que, setQue] = useState<'relocate' | 'cancel' | null>(null);
    const [motivo, setMotivo] = useState('');
    const [ocupado, setOcupado] = useState(false);
    const [estaba, setEstaba] = useState(abierta);
    if (abierta !== estaba) {
        setEstaba(abierta);
        if (abierta) {
            setCitas(null);
            setQue(null);
            setMotivo('');
        }
    }
    const nombre = profesional.display_name;
    const primero = primerNombre(nombre);
    const n = citas?.total ?? 0;

    const desactivar = () => {
        setOcupado(true);
        ops.deactivateProfessional(negocio, profesional.id, { ...(motivo.trim() === '' ? {} : { reason: motivo.trim() }), ...(que === null ? {} : { future_appointments: que }) })
            .then((p) => {
                avisar({
                    tipo: 'ok',
                    texto:
                        n === 0
                            ? `${nombre} desactivado. Ya no recibe citas ni cuenta para tu plan.`
                            : que === 'relocate'
                              ? `${nombre} desactivado. Sus ${String(n)} citas futuras están en Horarios › Citas afectadas: las que se pueden pasar a otro profesional ya tienen propuesta.`
                              : `${nombre} desactivado. Se han cancelado sus ${String(n)} citas futuras, devolviendo lo pagado, y se ha avisado a cada cliente.`,
                });
                onHecho(p);
                if (n > 0 && que === 'relocate') {
                    navegar(`${ruta}?apartado=horarios`);
                }
            })
            .catch((e: unknown) => {
                const pendientes = citasPendientes(e);
                if (pendientes !== null) {
                    setCitas(pendientes);
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e) });
            })
            .finally(() => { setOcupado(false); });
    };

    return (
        <Ventana
            abierta={abierta}
            titulo="Revisar el impacto y confirmar"
            texto={citas !== null && n > 0 ? `${nombre} deja de recibir citas nuevas desde hoy y de contar para tu plan. ¿Qué hacemos con las que ya tiene?` : `${nombre} deja de recibir citas y de contar para tu plan. Sus citas pasadas, cobros y notas se conservan.`}
            accion={n > 0 && que === 'cancel' ? 'Cancelar y desactivar' : 'Desactivar'}
            accionIcono="user-x"
            inactiva={n > 0 && que === null}
            ocupado={ocupado}
            ocupadoTexto="Desactivando…"
            ancho="560px"
            movil={movil}
            onAccion={desactivar}
            onCerrar={onCerrar}
        >
            {citas !== null && n > 0 && (
                <>
                    <div className="rv-sv-impacto">
                        <Cifra valor={n} tamano={28} />
                        <span>{n === 1 ? 'cita futura suya' : 'citas futuras suyas'}</span>
                    </div>
                    {citas.lineas.length > 0 && (
                        <div role="list" aria-label="Sus citas futuras" className="rv-sv-lineas">
                            {citas.lineas.map((l, i) => (
                                <div key={i} role="listitem" className="rv-sv-lineas__l">
                                    <span>{l.fecha === null ? '' : mayuscula(fechaLarga(l.fecha.slice(0, 10)))}</span>
                                    <span>{l.texto}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    <div role="radiogroup" aria-label="Qué hacemos con sus citas" className="rv-sv-grupo">
                        <OpcionRadio elegida={que === 'relocate'} titulo="Recolocarlas" texto={`Las de «cualquiera» pasan a otro profesional libre a la misma hora; las que eligieron a ${primero}, se habla con el cliente. Las ves en Horarios › Citas afectadas.`} onElegir={() => { setQue('relocate'); }} />
                        <OpcionRadio elegida={que === 'cancel'} titulo="Cancelarlas y devolver todo" texto="Se cancela cada cita, se devuelve lo pagado y se avisa a cada cliente." onElegir={() => { setQue('cancel'); }} />
                    </div>
                </>
            )}
            <Campo etiqueta="Motivo (opcional)" valor={motivo} onChange={setMotivo} max={500} ayuda="Queda en Actividad. No se le envía al cliente." mayusculas="sentences" />
        </Ventana>
    );
}

// ---- Dar acceso al panel ----

function VentanaAcceso({ ops, negocio, movil, profesional, abierta, onCerrar, onHecho }: Props & { profesional: Profesional; abierta: boolean; onHecho: (p: Profesional) => void }) {
    const avisar = useAvisoBreve();
    const [email, setEmail] = useState(profesional.email ?? '');
    const [error, setError] = useState<string | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const enviar = () => {
        if (!emailValido(email)) {
            setError('Escribe un email válido.');
            return;
        }
        setOcupado(true);
        ops.grantProfessionalPanelAccess(negocio, profesional.id, email.trim())
            .then((p) => {
                avisar({ tipo: 'ok', texto: `Invitación enviada a ${email.trim()}. Caduca a los 7 días.` });
                onHecho(p);
            })
            .catch((e: unknown) => { setError(textoDeError(e)); })
            .finally(() => { setOcupado(false); });
    };
    return (
        <Ventana
            abierta={abierta}
            titulo={`Dar acceso a ${profesional.display_name}`}
            texto="Le enviamos una invitación con rol Profesional. Caduca a los 7 días. Si ya tiene cuenta en Reservaria, entra con la suya."
            accion="Enviar la invitación"
            accionIcono="send"
            ocupado={ocupado}
            ocupadoTexto="Enviando…"
            movil={movil}
            onAccion={enviar}
            onCerrar={onCerrar}
        >
            <Campo
                etiqueta="Email"
                tipo="email"
                valor={email}
                onChange={(v) => {
                    setEmail(v);
                    setError(null);
                }}
                error={error}
                autocompletar="off"
            />
        </Ventana>
    );
}

// ---- Su horario (resumen; se cambia en Horarios) ----

function SuHorario({ ops, negocio, membresia, profesional, horario, gestiona, soloLectura, onHorarios }: Props & { profesional: Profesional }) {
    const [dias, setDias] = useState<string[] | null>(null);
    const [proximo, setProximo] = useState<string | null>(null);
    const hoy = hoyEn(membresia?.timezone);
    useEffect(() => {
        let vivo = true;
        ops.getEffectiveHours(negocio, profesional.calendar_id, hoy, sumarDias(hoy, 6))
            .then((h) => {
                const semana: string[] = Array.from({ length: 7 }, () => 'No trabaja');
                for (let i = 0; i < 7; i++) {
                    const f = sumarDias(hoy, i);
                    const t = tramosDelDia(h, f);
                    semana[diaDeLaSemana(f)] = t.length === 0 ? 'No trabaja' : t.map(([a, b]) => `${hm(a)} – ${hm(b)}`).join(' y ');
                }
                if (vivo) {
                    setDias(semana);
                }
            })
            .catch(() => {
                if (vivo) {
                    setDias([]);
                }
            });
        ops.listExceptions(negocio, profesional.calendar_id, hoy, sumarDias(hoy, 91), { limit: 20 })
            .then((l) => {
                const d = l.data.filter((x) => x.status === 'active').sort((a, b) => a.start_date.localeCompare(b.start_date))[0];
                if (vivo && d !== undefined) {
                    const cuando = d.start_date === d.end_date ? fechaLarga(d.start_date) : `del ${fechaLarga(d.start_date)} al ${fechaLarga(d.end_date)}`;
                    setProximo(`${d.reason !== undefined && d.reason !== '' ? `${d.reason.toLocaleLowerCase('es')}, ` : ''}${cuando}`);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, profesional.calendar_id, hoy]);

    const nota = `${horario === 'propio' ? 'Horario propio.' : horario === 'negocio' ? 'Usa el horario del negocio.' : horarioDe(horario)}${proximo === null ? '' : ` Próximo día especial: ${proximo}.`}`;
    return (
        <div className="rv-sv-form">
            <div className="rv-sv-lineas">
                {(dias ?? []).map((v, i) => (
                    <div key={DIAS[i]} className="rv-sv-lineas__l">
                        <span>{mayuscula(DIAS[i] ?? '')}</span>
                        <span style={{ color: v === 'No trabaja' ? 'var(--rv-fg-3)' : undefined }}>{v}</span>
                    </div>
                ))}
            </div>
            {nota.trim() !== '' && <p className="rv-sv-nota">{nota.trim()}</p>}
            <div>
                <Boton tipo="secundario" icono="clock" onClick={() => { onHorarios(profesional.id); }}>
                    {gestiona && !soloLectura && profesional.status === 'active' ? 'Cambiar su horario' : 'Ver en Horarios'}
                </Boton>
            </div>
        </div>
    );
}
